import React, { useState } from "react";
import { Container } from "react-bootstrap";
import { withRouter } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  T, EMPLOYEES, DEPARTMENTS, DESIGNATIONS, SERVICE_HISTORY, OPENINGS, CANDIDATES,
  ONBOARDING, ATTENDANCE_MONTH, LEAVE_TYPES, LEAVE_REQUESTS, HOLIDAYS, PAYSLIPS,
  SALARY_HISTORY, APPRAISALS, PROMOTIONS, DOCUMENTS, EXITS, CLEARANCE_ITEMS,
  ACTIVITIES, TT_DAYS, TT_PERIODS, TIMETABLE, LECTURER_WORKLOAD,
  StatusBadge, Avatar, Card, StatCard, SectionHeader, Btn, Modal,
  Field, Input, Select, inputStyle, Toast, DataTable, Info,
} from "./hrData";

const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");
const PIE_COLORS = [T.gold, T.teal, T.info, T.coral];

/* ─── Tab definitions (order = sidebar order) ────────────────────── */
export const HR_TABS = [
  { key: "dashboard", label: "Dashboard", icon: "fas fa-tachometer-alt" },
  { key: "employees", label: "Employee Management", icon: "fas fa-users" },
  { key: "departments", label: "Departments & Designations", icon: "fas fa-sitemap" },
  { key: "attendance", label: "Attendance", icon: "fas fa-clipboard-check" },
  { key: "timetable", label: "Lecturer Time Table", icon: "fas fa-calendar-alt" },
  { key: "leave", label: "Leave Management", icon: "fas fa-plane-departure" },
  { key: "payroll", label: "Payroll", icon: "fas fa-wallet" },
  { key: "performance", label: "Performance", icon: "fas fa-award" },
  { key: "recruitment", label: "Recruitment & Onboarding", icon: "fas fa-user-plus" },
  { key: "documents", label: "Employee Documents", icon: "fas fa-folder-open" },
  { key: "ess", label: "Self-Service (ESS)", icon: "fas fa-id-badge" },
  { key: "reports", label: "Reports & Analytics", icon: "fas fa-chart-bar" },
  { key: "exit", label: "Exit Management", icon: "fas fa-sign-out-alt" },
];
export const HR_TAB_KEYS = HR_TABS.map((t) => t.key);

const chartTip = { background: T.panel, border: `1px solid ${T.rule}`, borderRadius: 8, fontSize: 12 };
const grid3 = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14 };

/* ================================================================
   DASHBOARD
================================================================ */
function DashboardPanel({ navigate, showToast }) {
  const active = EMPLOYEES.filter((e) => e.status === "Active").length;
  const present = EMPLOYEES.filter((e) => e.attendanceToday === "Present").length;
  const onLeave = EMPLOYEES.filter((e) => e.attendanceToday === "On Leave").length;
  const teaching = EMPLOYEES.filter((e) => e.type === "Teaching" && e.status !== "Resigned").length;
  const pending = LEAVE_REQUESTS.filter((l) => l.status === "Pending").length;

  const deptData = DEPARTMENTS.map((d) => ({ name: d.code, staff: d.staff }));
  const leavePie = LEAVE_TYPES.map((l) => ({ name: l.type, value: l.used }));

  return (
    <div>
      <SectionHeader eyebrow="Overview" title="HR Dashboard" sub="Tuesday, 7 July 2026"
        action={<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn icon="fas fa-user-plus" onClick={() => navigate("recruitment")}>Recruitment</Btn>
          <Btn variant="gold" icon="fas fa-plane-departure" onClick={() => navigate("leave")}>Review leave</Btn>
        </div>} />

      <div style={{ ...grid3, marginBottom: 20 }}>
        <StatCard label="Active Staff" value={active} sub="+2 this month" accent={T.teal} icon="fas fa-users" />
        <StatCard label="Present Today" value={present} sub={`${Math.round((present / EMPLOYEES.length) * 100)}% attendance`} accent={T.gold} icon="fas fa-clipboard-check" />
        <StatCard label="On Leave Today" value={onLeave} sub={`${pending} pending approval`} accent={T.amber} icon="fas fa-plane-departure" />
        <StatCard label="Teaching Staff" value={teaching} sub={`${EMPLOYEES.length - teaching} non-teaching`} accent={T.info} icon="fas fa-chalkboard-teacher" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 16 }} className="hr-grid-2-1">
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Attendance trend — last 30 days</div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={ATTENDANCE_MONTH}>
                <defs>
                  <linearGradient id="hrPresent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={T.teal} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={T.teal} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.rule} vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: T.slate }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: T.slate }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={chartTip} />
                <Area type="monotone" dataKey="present" stroke={T.teal} strokeWidth={2} fill="url(#hrPresent)" name="Present" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Leave usage by type</div>
          <div style={{ height: 170 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={leavePie} dataKey="value" nameKey="name" innerRadius={40} outerRadius={66} paddingAngle={3}>
                  {leavePie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={chartTip} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: 8 }}>
            {leavePie.map((l, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 5 }}>
                <span style={{ color: T.slate, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: PIE_COLORS[i % PIE_COLORS.length] }} />{l.name}
                </span>
                <span style={{ color: T.ink }}>{l.value}d</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }} className="hr-grid-2-1">
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Headcount by department</div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deptData}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.rule} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: T.slate }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: T.slate }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: T.paper }} contentStyle={chartTip} />
                <Bar dataKey="staff" fill={T.gold} radius={[4, 4, 0, 0]} maxBarSize={34} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Recent activity</div>
          {ACTIVITIES.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 30, height: 30, borderRadius: 999, flexShrink: 0, background: T[a.tone] + "22", color: T[a.tone], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>
                <i className={a.icon} />
              </div>
              <div>
                <div style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.4 }}>{a.text}</div>
                <div style={{ fontSize: 11, color: T.slate, marginTop: 2 }}>{a.time}</div>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

/* ================================================================
   EMPLOYEE MANAGEMENT  (+ profile drawer + add/edit modal)
================================================================ */
function EmployeeProfileDrawer({ employee, onClose, onEdit }) {
  const [tab, setTab] = useState("Overview");
  const history = SERVICE_HISTORY[employee.id] || [{ date: employee.joinDate, event: `Joined as ${employee.designation} — ${employee.dept}` }];
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1050, display: "flex", justifyContent: "flex-end", background: "rgba(15,20,32,0.45)" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: "100%", height: "100%", background: T.panel, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "20px 22px", borderBottom: `1px solid ${T.rule}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <Avatar name={employee.name} size={48} />
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: T.ink, margin: 0 }}>{employee.name}</h3>
                <div style={{ fontSize: 12, color: T.slate }}>{employee.id}</div>
              </div>
            </div>
            <button onClick={onClose} style={{ border: "none", background: T.paper, borderRadius: 8, width: 30, height: 30, cursor: "pointer", color: T.ink }}><i className="fas fa-times" /></button>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <StatusBadge status={employee.status} />
            <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 999, background: T.paper, color: T.slate }}>{employee.type}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, padding: "10px 22px 0", borderBottom: `1px solid ${T.rule}` }}>
          {["Overview", "Documents", "History"].map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{ border: "none", background: "transparent", padding: "8px 12px", fontSize: 13, cursor: "pointer", color: tab === t ? T.ink : T.slate, fontWeight: tab === t ? 600 : 400, borderBottom: tab === t ? `2px solid ${T.gold}` : "2px solid transparent" }}>{t}</button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 22 }}>
          {tab === "Overview" && (
            <div style={{ display: "grid", gap: 16 }}>
              <Info label="Designation" value={`${employee.designation} · ${employee.dept}`} />
              <Info label="Qualification" value={`${employee.qualification} · ${employee.exp} yrs experience`} />
              <Info label="Phone" value={employee.phone} />
              <Info label="Email" value={employee.email} />
              <Info label="Address" value={employee.address} />
              <Info label="Date of joining" value={employee.joinDate} />
              <Info label="Monthly salary" value={inr(employee.salary)} />
            </div>
          )}
          {tab === "Documents" && (
            <div style={{ display: "grid", gap: 8 }}>
              {["Aadhaar", "PAN Card", "Educational Certificates", "Appointment Letter"].map((d, i) => (
                <div key={d} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: 9, background: T.paper }}>
                  <span style={{ fontSize: 13, color: T.ink }}><i className="fas fa-file-alt" style={{ color: T.slate, marginRight: 8 }} />{d}</span>
                  <StatusBadge status={i < 3 ? "Verified" : "Pending"} />
                </div>
              ))}
            </div>
          )}
          {tab === "History" && (
            <div>
              {history.map((h, i) => (
                <div key={i} style={{ position: "relative", paddingLeft: 22, paddingBottom: 20 }}>
                  {i < history.length - 1 && <div style={{ position: "absolute", left: 5, top: 12, bottom: 0, width: 1, background: T.rule }} />}
                  <div style={{ position: "absolute", left: 0, top: 5, width: 10, height: 10, borderRadius: 999, background: T.gold }} />
                  <div style={{ fontSize: 12, color: T.slate }}>{h.date}</div>
                  <div style={{ fontSize: 13, color: T.ink }}>{h.event}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, padding: 18, borderTop: `1px solid ${T.rule}` }}>
          <button onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 9, border: "none", background: T.paper, color: T.ink, fontWeight: 600, cursor: "pointer" }}>Close</button>
          <button onClick={onEdit} style={{ flex: 1, padding: 10, borderRadius: 9, border: "none", background: T.ink, color: "#fff", fontWeight: 600, cursor: "pointer" }}>Edit record</button>
        </div>
      </div>
    </div>
  );
}

function EmployeeFormModal({ open, onClose, onSave, initial }) {
  const blank = { name: "", dept: DEPARTMENTS[0].name, designation: DESIGNATIONS[0].title, email: "", phone: "", type: "Teaching", status: "Active" };
  const [form, setForm] = useState(initial || blank);
  React.useEffect(() => { setForm(initial || blank); /* eslint-disable-next-line */ }, [initial, open]);
  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit employee" : "Register new employee"}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Field label="Full name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Kavitha Reddy" /></Field>
        <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@rizee.in" /></Field>
        <Field label="Department"><Select value={form.dept} onChange={(e) => setForm({ ...form, dept: e.target.value })}>{DEPARTMENTS.map((d) => <option key={d.id}>{d.name}</option>)}</Select></Field>
        <Field label="Designation"><Select value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })}>{DESIGNATIONS.map((d) => <option key={d.id}>{d.title}</option>)}</Select></Field>
        <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91" /></Field>
        <Field label="Staff type"><Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{["Teaching", "Non-Teaching"].map((s) => <option key={s}>{s}</option>)}</Select></Field>
        <Field label="Status"><Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{["Active", "Probation", "On Leave", "Resigned"].map((s) => <option key={s}>{s}</option>)}</Select></Field>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={() => onSave(form)}>{initial ? "Save changes" : "Register employee"}</Btn>
      </div>
    </Modal>
  );
}

function EmployeesPanel({ employees, setEmployees, showToast }) {
  const [view, setView] = useState(null);
  const [modal, setModal] = useState(null); // "add" | employeeObj | null

  // Subject a teaching employee handles, derived from their department (same
  // mapping the Lecturer Workload table uses). Non-teaching staff have none.
  const subjectForEmployee = (r) => {
    if (r.type === "Non-Teaching") return "—";
    switch (r.dept) {
      case "Mathematics": return "Maths";
      case "Biology": return "Botany / Zoology";
      default: return r.dept || "—";
    }
  };

  const columns = [
    { key: "name", label: "Employee", render: (r) => (
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Avatar name={r.name} size={34} />
        <div><div style={{ fontWeight: 600, color: T.ink }}>{r.name}</div><div style={{ fontSize: 11, color: T.slate }}>{r.id}</div></div>
      </div>
    ) },
    { key: "designation", label: "Designation", render: (r) => (<div><div>{r.designation}</div><div style={{ fontSize: 12, color: T.slate }}>{r.dept}</div></div>) },
    { key: "subject", label: "Subject", render: (r) => {
      const subj = subjectForEmployee(r);
      return subj === "—"
        ? <span style={{ color: T.slate }}>—</span>
        : <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#eef2ff", color: "#4338ca", borderRadius: 6, padding: "2px 8px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}><i className="fas fa-book" />{subj}</span>;
    } },
    { key: "joinDate", label: "Joined" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div>
      <SectionHeader eyebrow={`${employees.length} records`} title="Employee Management"
        action={<div style={{ display: "flex", gap: 8 }}>
          <Btn icon="fas fa-download" onClick={() => showToast("Employee list exported")}>Export</Btn>
          <Btn variant="primary" icon="fas fa-plus" onClick={() => setModal("add")}>Add employee</Btn>
        </div>} />
      <Card style={{ padding: 18 }}>
        <DataTable columns={columns} data={employees} searchKeys={["name", "id", "email"]}
          filters={[{ key: "dept", options: DEPARTMENTS.map((d) => d.name) }, { key: "type", options: ["Teaching", "Non-Teaching"] }, { key: "status", options: ["Active", "Probation", "On Leave", "Resigned"] }]}
          onRowClick={(r) => setView(r)}
          rowActions={(r) => (
            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
              <button onClick={() => setView(r)} title="View" style={iconBtn}><i className="fas fa-eye" /></button>
              <button onClick={() => setModal(r)} title="Edit" style={iconBtn}><i className="fas fa-pen" /></button>
            </div>
          )} />
      </Card>

      {view && <EmployeeProfileDrawer employee={view} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} />}
      <EmployeeFormModal open={!!modal} onClose={() => setModal(null)} initial={modal && modal !== "add" ? modal : null}
        onSave={(form) => {
          if (modal === "add") {
            const id = `RZ-${form.type === "Teaching" ? "T" : "N"}-${300 + employees.length}`;
            setEmployees([{ ...form, id, joinDate: "2026-07-07", exp: 0, salary: 35000, qualification: "—", attendanceToday: "Present", gender: "—", address: "—" }, ...employees]);
            showToast("Employee registered");
          } else {
            setEmployees(employees.map((e) => (e.id === modal.id ? { ...e, ...form } : e)));
            showToast("Employee updated");
          }
          setModal(null);
        }} />
    </div>
  );
}
const iconBtn = { border: "none", background: "transparent", cursor: "pointer", color: T.slate, padding: 6, borderRadius: 8, fontSize: 14 };

/* ================================================================
   DEPARTMENTS & DESIGNATIONS
================================================================ */
function DepartmentsPanel({ showToast }) {
  const [tab, setTab] = useState("departments");
  return (
    <div>
      <SectionHeader eyebrow="Structure" title="Departments & Designations"
        action={<Btn variant="primary" icon="fas fa-plus" onClick={() => showToast(`Opens add-${tab === "departments" ? "department" : "designation"} form`)}>Add {tab === "departments" ? "department" : "designation"}</Btn>} />
      <PillTabs tabs={[["departments", "Departments"], ["designations", "Designations"], ["hierarchy", "Reporting hierarchy"]]} active={tab} onChange={setTab} />

      {tab === "departments" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
          {DEPARTMENTS.map((d) => (
            <Card key={d.id} style={{ padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.tealSoft, color: T.teal, display: "flex", alignItems: "center", justifyContent: "center" }}><i className="fas fa-building" /></div>
                <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 6, background: T.paper, color: T.slate }}>{d.code}</span>
              </div>
              <div style={{ fontWeight: 600, color: T.ink, marginBottom: 4 }}>{d.name}</div>
              <div style={{ fontSize: 12, color: T.slate, marginBottom: 12 }}>Head: {d.head}</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: `1px solid ${T.rule}`, fontSize: 12, color: T.slate }}>
                <span>{d.staff} staff members</span>
                <span><i className="fas fa-pen" style={{ marginRight: 12, cursor: "pointer" }} /><i className="fas fa-trash-alt" style={{ color: T.coral, cursor: "pointer" }} /></span>
              </div>
            </Card>
          ))}
        </div>
      )}
      {tab === "designations" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[{ key: "title", label: "Designation" }, { key: "dept", label: "Department" }, { key: "level", label: "Level", render: (r) => `L${r.level}` }, { key: "reportsTo", label: "Reports to" }]} data={DESIGNATIONS} searchKeys={["title", "dept"]} />
        </Card>
      )}
      {tab === "hierarchy" && (
        <Card style={{ padding: 22 }}>
          {DESIGNATIONS.slice().sort((a, b) => a.level - b.level).map((d, i) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", paddingLeft: (d.level - 1) * 28, borderBottom: i < DESIGNATIONS.length - 1 ? `1px solid ${T.rule}` : "none" }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: T.gold, flexShrink: 0 }} />
              <span style={{ fontWeight: 600, fontSize: 13, color: T.ink }}>{d.title}</span>
              <span style={{ fontSize: 12, color: T.slate }}>{d.dept}</span>
              {d.reportsTo !== "—" && <span style={{ fontSize: 12, color: T.slate, marginLeft: "auto" }}>reports to {d.reportsTo}</span>}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/* ================================================================
   ATTENDANCE
================================================================ */
function AttendancePanel({ employees, showToast }) {
  const [tab, setTab] = useState("daily");
  const [manual, setManual] = useState(false);
  const counts = {
    Present: employees.filter((e) => e.attendanceToday === "Present").length,
    Late: employees.filter((e) => e.attendanceToday === "Late").length,
    "On Leave": employees.filter((e) => e.attendanceToday === "On Leave").length,
  };
  return (
    <div>
      <SectionHeader eyebrow="Time & presence" title="Attendance Management"
        action={<div style={{ display: "flex", gap: 8 }}>
          <Btn icon="fas fa-fingerprint" onClick={() => showToast("Synced with biometric devices")}>Sync biometric</Btn>
          <Btn variant="primary" icon="fas fa-plus" onClick={() => setManual(true)}>Mark manually</Btn>
        </div>} />
      <div style={{ ...grid3, marginBottom: 18 }}>
        <StatCard label="Present" value={counts.Present} accent={T.teal} icon="fas fa-check" />
        <StatCard label="Late" value={counts.Late} accent={T.amber} icon="fas fa-clock" />
        <StatCard label="On Leave" value={counts["On Leave"]} accent={T.info} icon="fas fa-plane-departure" />
        <StatCard label="Face-ID check-ins" value={9} accent={T.gold} icon="fas fa-user-check" />
      </div>
      <PillTabs tabs={[["daily", "Daily attendance"], ["calendar", "Calendar"], ["monthly", "Monthly report"]]} active={tab} onChange={setTab} />

      {tab === "daily" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "name", label: "Employee", render: (r) => <span style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={r.name} size={30} />{r.name}</span> },
            { key: "dept", label: "Department" },
            { key: "attendanceToday", label: "Status", render: (r) => <StatusBadge status={r.attendanceToday} /> },
            { key: "checkin", label: "Check-in", sortable: false, render: (r) => r.attendanceToday === "On Leave" ? "—" : r.attendanceToday === "Late" ? "10:12 AM" : "09:0" + (r.id.length % 9) + " AM" },
            { key: "method", label: "Method", sortable: false, render: (r) => r.attendanceToday === "On Leave" ? "—" : (r.id.charCodeAt(4) % 2 === 0 ? <span><i className="fas fa-user-check" style={{ marginRight: 6 }} />Face ID</span> : <span><i className="fas fa-fingerprint" style={{ marginRight: 6 }} />Biometric</span>) },
          ]} data={employees} searchKeys={["name", "dept"]} filters={[{ key: "attendanceToday", options: ["Present", "Late", "On Leave"] }]} />
        </Card>
      )}
      {tab === "calendar" && (
        <Card style={{ padding: 22 }}>
          <div style={{ fontWeight: 700, color: T.ink, marginBottom: 14 }}>July 2026</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, textAlign: "center", fontSize: 11, color: T.slate, marginBottom: 6 }}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
            {Array.from({ length: 31 }).map((_, i) => {
              const day = i + 1;
              const rate = 88 + ((i * 7) % 10);
              const isHoliday = HOLIDAYS.some((h) => h.date.startsWith(String(day).padStart(2, "0") + "-Jul"));
              const isWeekend = (i + 2) % 7 === 0 || (i + 3) % 7 === 0;
              return (
                <div key={i} style={{ borderRadius: 9, padding: "8px 4px", textAlign: "center", background: isHoliday ? T.coralSoft : isWeekend ? T.paper : T.panel, border: `1px solid ${T.rule}` }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.ink }}>{day}</div>
                  {!isWeekend && !isHoliday && <div style={{ fontSize: 10, marginTop: 3, color: T.teal }}>{rate}%</div>}
                  {isHoliday && <div style={{ fontSize: 10, marginTop: 3, color: T.coral }}>Holiday</div>}
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {tab === "monthly" && (
        <Card style={{ padding: 20 }}>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ATTENDANCE_MONTH}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.rule} vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: T.slate }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: T.slate }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={chartTip} />
                <Bar dataKey="present" stackId="a" fill={T.teal} name="Present" />
                <Bar dataKey="late" stackId="a" fill={T.gold} name="Late" />
                <Bar dataKey="absent" stackId="a" fill={T.coral} radius={[4, 4, 0, 0]} name="Absent" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
      <Modal open={manual} onClose={() => setManual(false)} title="Mark attendance manually">
        <Field label="Employee"><Select>{employees.slice(0, 10).map((e) => <option key={e.id}>{e.name}</option>)}</Select></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Date"><Input type="date" defaultValue="2026-07-07" /></Field>
          <Field label="Status"><Select>{["Present", "Absent", "Late", "Half day"].map((s) => <option key={s}>{s}</option>)}</Select></Field>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn onClick={() => setManual(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={() => { showToast("Attendance recorded"); setManual(false); }}>Save</Btn>
        </div>
      </Modal>
    </div>
  );
}

/* ================================================================
   LEAVE MANAGEMENT
================================================================ */
function LeavePanel({ showToast }) {
  const [tab, setTab] = useState("approvals");
  const [requests, setRequests] = useState(LEAVE_REQUESTS);
  const [apply, setApply] = useState(false);
  return (
    <div>
      <SectionHeader eyebrow="Time off" title="Leave Management"
        action={<Btn variant="primary" icon="fas fa-plus" onClick={() => setApply(true)}>Apply for leave</Btn>} />
      <div style={{ ...grid3, marginBottom: 18 }}>
        {LEAVE_TYPES.map((l) => (
          <Card key={l.type} style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: T.slate, marginBottom: 8 }}>{l.type}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.ink, marginBottom: 8 }}>{l.total - l.used}<span style={{ fontSize: 12, fontWeight: 400, color: T.slate, marginLeft: 4 }}>days left</span></div>
            <div style={{ height: 6, borderRadius: 999, background: T.rule }}><div style={{ height: 6, borderRadius: 999, width: `${(l.used / l.total) * 100}%`, background: T.gold }} /></div>
          </Card>
        ))}
      </div>
      <PillTabs tabs={[["approvals", "Approval workflow"], ["history", "Leave history"], ["holidays", "Holiday calendar"]]} active={tab} onChange={setTab} />

      {tab === "approvals" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "employee", label: "Employee" }, { key: "type", label: "Leave type" }, { key: "from", label: "From" }, { key: "to", label: "To" }, { key: "days", label: "Days" },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]} data={requests} searchKeys={["employee", "type"]} filters={[{ key: "status", options: ["Pending", "Approved", "Rejected"] }]}
            rowActions={(r) => r.status === "Pending" ? (
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <button onClick={() => { setRequests(requests.map((x) => x.id === r.id ? { ...x, status: "Approved" } : x)); showToast(`Approved ${r.employee}'s leave`); }} style={{ ...iconBtn, color: T.teal, background: T.tealSoft }}><i className="fas fa-check" /></button>
                <button onClick={() => { setRequests(requests.map((x) => x.id === r.id ? { ...x, status: "Rejected" } : x)); showToast(`Rejected ${r.employee}'s leave`, "error"); }} style={{ ...iconBtn, color: T.coral, background: T.coralSoft }}><i className="fas fa-times" /></button>
              </div>
            ) : <span style={{ fontSize: 12, color: T.slate }}>—</span>} />
        </Card>
      )}
      {tab === "history" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[{ key: "employee", label: "Employee" }, { key: "type", label: "Type" }, { key: "reason", label: "Reason" }, { key: "days", label: "Days" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> }]} data={requests} searchKeys={["employee", "reason"]} />
        </Card>
      )}
      {tab === "holidays" && (
        <Card style={{ padding: 0 }}>
          {HOLIDAYS.map((h, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: i < HOLIDAYS.length - 1 ? `1px solid ${T.rule}` : "none" }}>
              <span style={{ fontSize: 13, color: T.ink }}><i className="fas fa-calendar-day" style={{ color: T.gold, marginRight: 10 }} />{h.name}</span>
              <span style={{ fontSize: 13, color: T.slate }}>{h.date}</span>
            </div>
          ))}
        </Card>
      )}
      <Modal open={apply} onClose={() => setApply(false)} title="Apply for leave">
        <Field label="Leave type"><Select>{LEAVE_TYPES.map((l) => <option key={l.type}>{l.type}</option>)}</Select></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="From"><Input type="date" /></Field>
          <Field label="To"><Input type="date" /></Field>
        </div>
        <Field label="Reason"><textarea rows={3} style={inputStyle} placeholder="Brief reason for leave" /></Field>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn onClick={() => setApply(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={() => { showToast("Leave application submitted"); setApply(false); }}>Submit request</Btn>
        </div>
      </Modal>
    </div>
  );
}

/* ================================================================
   PAYROLL
================================================================ */
function PayrollPanel({ employees, showToast }) {
  const [tab, setTab] = useState("structure");
  const [process, setProcess] = useState(false);
  const activeEmps = employees.filter((e) => e.status === "Active");
  return (
    <div>
      <SectionHeader eyebrow="Compensation" title="Payroll Management"
        action={<Btn variant="gold" icon="fas fa-wallet" onClick={() => setProcess(true)}>Process payroll</Btn>} />
      <PillTabs tabs={[["structure", "Salary structure"], ["payslips", "Payslips"], ["history", "Salary history"]]} active={tab} onChange={setTab} />

      {tab === "structure" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "name", label: "Employee" }, { key: "designation", label: "Designation" },
            { key: "basic", label: "Basic", sortable: false, render: (r) => inr(Math.round(r.salary * 0.5)) },
            { key: "hra", label: "HRA", sortable: false, render: (r) => inr(Math.round(r.salary * 0.2)) },
            { key: "allow", label: "Allowances", sortable: false, render: (r) => inr(Math.round(r.salary * 0.18)) },
            { key: "ded", label: "Deductions", sortable: false, render: (r) => inr(Math.round(r.salary * 0.12)) },
            { key: "salary", label: "Gross", render: (r) => <strong>{inr(r.salary)}</strong> },
          ]} data={activeEmps} searchKeys={["name"]} />
        </Card>
      )}
      {tab === "payslips" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "employee", label: "Employee" }, { key: "month", label: "Month" },
            { key: "gross", label: "Gross", render: (r) => inr(r.gross) }, { key: "deductions", label: "Deductions", render: (r) => inr(r.deductions) },
            { key: "net", label: "Net pay", render: (r) => <strong>{inr(r.net)}</strong> }, { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]} data={PAYSLIPS} searchKeys={["employee"]} rowActions={(r) => <button onClick={() => showToast(`Downloading payslip — ${r.employee}`)} style={iconBtn}><i className="fas fa-download" /></button>} />
        </Card>
      )}
      {tab === "history" && (
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Net payroll disbursed — last 6 months</div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={SALARY_HISTORY}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.rule} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: T.slate }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: T.slate }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={chartTip} formatter={(v) => inr(v)} />
                <Line type="monotone" dataKey="net" stroke={T.gold} strokeWidth={2.5} dot={{ r: 4, fill: T.gold }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
      <Modal open={process} onClose={() => setProcess(false)} title="Process payroll" width={440}>
        <p style={{ fontSize: 13, color: T.slate, marginBottom: 16 }}>Run payroll for <strong style={{ color: T.ink }}>July 2026</strong> covering {activeEmps.length} active employees.</p>
        <div style={{ borderRadius: 12, padding: 16, marginBottom: 16, background: T.paper }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8 }}><span style={{ color: T.slate }}>Total gross</span><span style={{ color: T.ink }}>{inr(activeEmps.reduce((s, e) => s + e.salary, 0))}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: T.slate }}>Total net payable</span><span style={{ fontWeight: 600, color: T.ink }}>{inr(Math.round(activeEmps.reduce((s, e) => s + e.salary, 0) * 0.88))}</span></div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn onClick={() => setProcess(false)}>Cancel</Btn>
          <Btn variant="gold" onClick={() => { showToast("Payroll processed for July 2026"); setProcess(false); }}>Confirm & process</Btn>
        </div>
      </Modal>
    </div>
  );
}

/* ================================================================
   PERFORMANCE
================================================================ */
function PerformancePanel({ showToast }) {
  const [tab, setTab] = useState("appraisals");
  return (
    <div>
      <SectionHeader eyebrow="Growth" title="Performance Management"
        action={<Btn variant="primary" icon="fas fa-plus" onClick={() => showToast("Opens new appraisal cycle form")}>New appraisal cycle</Btn>} />
      <PillTabs tabs={[["appraisals", "Appraisals"], ["kpi", "KPI tracking"], ["promotions", "Promotion history"]]} active={tab} onChange={setTab} />

      {tab === "appraisals" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "employee", label: "Employee" }, { key: "dept", label: "Department" }, { key: "cycle", label: "Cycle" },
            { key: "rating", label: "Rating", render: (r) => <span><i className="fas fa-star" style={{ color: T.gold, marginRight: 5 }} />{r.rating}</span> },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]} data={APPRAISALS} searchKeys={["employee", "dept"]} />
        </Card>
      )}
      {tab === "kpi" && (
        <Card style={{ padding: 0 }}>
          {APPRAISALS.map((a, i) => (
            <div key={i} style={{ padding: "16px 20px", borderBottom: i < APPRAISALS.length - 1 ? `1px solid ${T.rule}` : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{a.employee}</span>
                <span style={{ fontSize: 12, color: T.slate }}>{a.kpi}% of target</span>
              </div>
              <div style={{ height: 8, borderRadius: 999, background: T.rule }}><div style={{ height: 8, borderRadius: 999, width: `${a.kpi}%`, background: a.kpi >= 85 ? T.teal : a.kpi >= 70 ? T.gold : T.coral }} /></div>
            </div>
          ))}
        </Card>
      )}
      {tab === "promotions" && (
        <Card style={{ padding: 22 }}>
          {PROMOTIONS.map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: 32, height: 32, borderRadius: 999, background: T.tealSoft, color: T.teal, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><i className="fas fa-award" /></div>
                {i < PROMOTIONS.length - 1 && <div style={{ width: 1, flex: 1, marginTop: 4, background: T.rule }} />}
              </div>
              <div style={{ paddingBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{p.employee}</div>
                <div style={{ fontSize: 12, color: T.slate, marginTop: 2 }}>{p.from} → {p.to}</div>
                <div style={{ fontSize: 12, color: T.slate, marginTop: 2 }}>{p.date}</div>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/* ================================================================
   RECRUITMENT & ONBOARDING
================================================================ */
function RecruitmentPanel({ showToast }) {
  const [tab, setTab] = useState("openings");
  return (
    <div>
      <SectionHeader eyebrow="Hiring pipeline" title="Recruitment & Onboarding"
        action={<Btn variant="primary" icon="fas fa-plus" onClick={() => showToast("Opens new job requisition form")}>Post opening</Btn>} />
      <div style={{ ...grid3, marginBottom: 18 }}>
        <StatCard label="Open positions" value={OPENINGS.length} accent={T.gold} icon="fas fa-briefcase" />
        <StatCard label="Total applicants" value={OPENINGS.reduce((s, o) => s + o.applicants, 0)} accent={T.info} icon="fas fa-users" />
        <StatCard label="Interviews scheduled" value={CANDIDATES.filter((c) => c.stage === "Interview").length} accent={T.teal} icon="fas fa-calendar-check" />
        <StatCard label="Onboarding pending" value={ONBOARDING.filter((o) => o.status === "Pending").length} accent={T.amber} icon="fas fa-user-clock" />
      </div>
      <PillTabs tabs={[["openings", "Job openings"], ["candidates", "Candidates"], ["onboarding", "Onboarding"]]} active={tab} onChange={setTab} />

      {tab === "openings" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "role", label: "Role" }, { key: "dept", label: "Department" }, { key: "type", label: "Type" },
            { key: "applicants", label: "Applicants" }, { key: "stage", label: "Stage", render: (r) => <StatusBadge status={r.stage} /> },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]} data={OPENINGS} searchKeys={["role", "dept"]} />
        </Card>
      )}
      {tab === "candidates" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "name", label: "Candidate", render: (r) => <span style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={r.name} size={30} />{r.name}</span> },
            { key: "role", label: "Applied for" }, { key: "stage", label: "Stage", render: (r) => <StatusBadge status={r.stage} /> },
            { key: "score", label: "Score", render: (r) => `${r.score}/100` }, { key: "applied", label: "Applied" },
          ]} data={CANDIDATES} searchKeys={["name", "role"]} filters={[{ key: "stage", options: ["Screening", "Interview", "Offer"] }]} />
        </Card>
      )}
      {tab === "onboarding" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "name", label: "New hire" }, { key: "task", label: "Task" }, { key: "due", label: "Due date" },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]} data={ONBOARDING} searchKeys={["name", "task"]}
            rowActions={(r) => r.status === "Pending" ? <button onClick={() => showToast(`Marked "${r.task}" complete`)} style={{ ...iconBtn, color: T.teal, background: T.tealSoft }}><i className="fas fa-check" /></button> : <span style={{ fontSize: 12, color: T.slate }}>—</span>} />
        </Card>
      )}
    </div>
  );
}

/* ================================================================
   DOCUMENTS
================================================================ */
function DocumentsPanel({ showToast }) {
  return (
    <div>
      <SectionHeader eyebrow="Records" title="Employee Documents"
        action={<Btn variant="primary" icon="fas fa-upload" onClick={() => showToast("Opens document upload dialog")}>Upload document</Btn>} />
      <Card style={{ padding: 24, marginBottom: 18, border: `2px dashed ${T.rule}`, background: T.paper, textAlign: "center" }}>
        <i className="fas fa-cloud-upload-alt" style={{ fontSize: 26, color: T.slate }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginTop: 12 }}>Drag files here or click to upload</div>
        <div style={{ fontSize: 12, color: T.slate, margin: "4px 0 14px" }}>PDF, JPG, PNG up to 10MB</div>
        <Btn onClick={() => showToast("Opens file picker")}>Choose file</Btn>
      </Card>
      <Card style={{ padding: 18 }}>
        <DataTable columns={[
          { key: "name", label: "Document", render: (r) => <span><i className="fas fa-file-alt" style={{ color: T.slate, marginRight: 8 }} />{r.name}</span> },
          { key: "employee", label: "Employee" }, { key: "type", label: "Category" },
          { key: "status", label: "Verification", render: (r) => <StatusBadge status={r.status} /> },
        ]} data={DOCUMENTS} searchKeys={["name", "employee"]} filters={[{ key: "status", options: ["Verified", "Pending", "Rejected"] }]}
          rowActions={(r) => (
            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
              <button onClick={() => showToast(`Previewing ${r.name}`)} style={iconBtn}><i className="fas fa-eye" /></button>
              <button onClick={() => showToast(`Downloading ${r.name}`)} style={iconBtn}><i className="fas fa-download" /></button>
            </div>
          )} />
      </Card>
    </div>
  );
}

/* ================================================================
   SELF-SERVICE (ESS)
================================================================ */
function SelfServicePanel({ employees, showToast }) {
  const me = employees[0];
  return (
    <div>
      <SectionHeader eyebrow="My workspace" title="Employee Self-Service (ESS)" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
        <Card style={{ padding: 22, textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><Avatar name={me.name} size={60} /></div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>{me.name}</div>
          <div style={{ fontSize: 12, color: T.slate, marginBottom: 14 }}>{me.designation} · {me.dept}</div>
          <Btn size="sm" onClick={() => showToast("Opens profile editor")}>Edit profile</Btn>
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 10 }}><i className="fas fa-clipboard-check" style={{ marginRight: 8, color: T.gold }} />This month's attendance</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: T.ink }}>21<span style={{ fontSize: 13, fontWeight: 400, color: T.slate }}> / 23 days</span></div>
          <div style={{ fontSize: 12, color: T.teal, marginTop: 4 }}>91% attendance this month</div>
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 10 }}><i className="fas fa-plane-departure" style={{ marginRight: 8, color: T.gold }} />Leave balance</div>
          {LEAVE_TYPES.slice(0, 3).map((l) => (
            <div key={l.type} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
              <span style={{ color: T.slate }}>{l.type}</span><span style={{ color: T.ink }}>{l.total - l.used} left</span>
            </div>
          ))}
        </Card>
        <Card style={{ padding: 0, gridColumn: "span 2" }} className="hr-span-2">
          <div style={{ padding: "18px 20px 8px", fontSize: 13, fontWeight: 600, color: T.ink }}><i className="fas fa-wallet" style={{ marginRight: 8, color: T.gold }} />Recent payslips</div>
          {PAYSLIPS.slice(0, 3).map((p, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.rule}` }}>
              <div><div style={{ fontSize: 13, color: T.ink }}>{p.month}</div><div style={{ fontSize: 12, color: T.slate }}>Net {inr(p.net)}</div></div>
              <button onClick={() => showToast(`Downloading payslip — ${p.month}`)} style={iconBtn}><i className="fas fa-download" /></button>
            </div>
          ))}
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 12 }}><i className="fas fa-bullhorn" style={{ marginRight: 8, color: T.gold }} />Quick requests</div>
          {[["Apply leave", "fas fa-plane-departure"], ["Download payslip", "fas fa-file-invoice-dollar"], ["Update details", "fas fa-user-edit"], ["Raise ticket", "fas fa-headset"]].map(([l, ic]) => (
            <button key={l} onClick={() => showToast(`${l} — opened`)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "10px 12px", marginBottom: 6, border: `1px solid ${T.rule}`, borderRadius: 9, background: T.paper, cursor: "pointer", fontSize: 13, color: T.ink }}>
              <i className={ic} style={{ color: T.gold }} />{l}
            </button>
          ))}
        </Card>
      </div>
    </div>
  );
}

/* ================================================================
   REPORTS & ANALYTICS
================================================================ */
function ReportsPanel({ showToast }) {
  const reports = [
    { title: "Attendance report", desc: "Daily and monthly attendance across all departments", icon: "fas fa-clipboard-check", tone: "teal" },
    { title: "Leave report", desc: "Leave usage, balances, and approval turnaround", icon: "fas fa-plane-departure", tone: "info" },
    { title: "Payroll report", desc: "Salary disbursement, deductions and tax summaries", icon: "fas fa-wallet", tone: "gold" },
    { title: "Department report", desc: "Headcount and cost breakdown by department", icon: "fas fa-building", tone: "coral" },
  ];
  return (
    <div>
      <SectionHeader eyebrow="Insights" title="Reports & Analytics" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginBottom: 18 }}>
        {reports.map((r) => (
          <Card key={r.title} style={{ padding: 18 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: T[r.tone] + "22", color: T[r.tone], display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}><i className={r.icon} /></div>
            <div style={{ fontWeight: 600, color: T.ink, marginBottom: 4 }}>{r.title}</div>
            <div style={{ fontSize: 12, color: T.slate, marginBottom: 14 }}>{r.desc}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn size="sm" icon="fas fa-file-pdf" onClick={() => showToast(`${r.title} exported as PDF`)}>PDF</Btn>
              <Btn size="sm" icon="fas fa-file-excel" onClick={() => showToast(`${r.title} exported as Excel`)}>Excel</Btn>
            </div>
          </Card>
        ))}
      </div>
      <Card style={{ padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Department-wise employee count</div>
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={DEPARTMENTS} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.rule} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: T.slate }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="code" width={44} tick={{ fontSize: 11, fill: T.slate }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={chartTip} />
              <Bar dataKey="staff" fill={T.teal} radius={[0, 4, 4, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

/* ================================================================
   EXIT MANAGEMENT
================================================================ */
function ExitPanel({ showToast }) {
  const [tab, setTab] = useState("exits");
  return (
    <div>
      <SectionHeader eyebrow="Offboarding" title="Exit Management"
        action={<Btn variant="primary" icon="fas fa-plus" onClick={() => showToast("Opens exit initiation form")}>Initiate exit</Btn>} />
      <PillTabs tabs={[["exits", "Exit requests"], ["clearance", "Clearance checklist"]]} active={tab} onChange={setTab} />

      {tab === "exits" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "employee", label: "Employee" }, { key: "dept", label: "Department" }, { key: "type", label: "Exit type" },
            { key: "lastDay", label: "Last working day" }, { key: "clearance", label: "Clearance", render: (r) => <StatusBadge status={r.clearance} /> },
          ]} data={EXITS} searchKeys={["employee", "dept"]} />
        </Card>
      )}
      {tab === "clearance" && (
        <Card style={{ padding: 0 }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.rule}`, fontSize: 13, fontWeight: 600, color: T.ink }}>
            Clearance for Naveen Chowdary · Last day 2026-07-31
          </div>
          {CLEARANCE_ITEMS.map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: i < CLEARANCE_ITEMS.length - 1 ? `1px solid ${T.rule}` : "none" }}>
              <div>
                <div style={{ fontSize: 13, color: T.ink }}>{c.item}</div>
                <div style={{ fontSize: 12, color: T.slate }}>Owner: {c.owner}</div>
              </div>
              <StatusBadge status={c.status} />
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/* ================================================================
   LECTURER TIME TABLE & WORKLOAD
================================================================ */
function TimeTablePanel({ showToast }) {
  const [tab, setTab] = useState("timetable");
  const lecturers = LECTURER_WORKLOAD.filter((l) => TIMETABLE[l.id]);
  const [lecturerId, setLecturerId] = useState(lecturers[0].id);
  const grid = TIMETABLE[lecturerId] || {};
  const lecturer = LECTURER_WORKLOAD.find((l) => l.id === lecturerId);

  const filled = TT_DAYS.reduce((sum, d) => sum + (grid[d] || []).filter(Boolean).length, 0);
  const totalSlots = TT_DAYS.length * TT_PERIODS.length;

  // subtle per-subject tint so the grid reads at a glance
  const tint = (s) => {
    if (!s) return { bg: T.paper, fg: T.slate };
    if (/Lab/i.test(s.subject)) return { bg: T.infoSoft, fg: T.info };
    if (/Test|Doubt|Mentoring|Meeting/i.test(s.subject)) return { bg: T.amberSoft, fg: T.amber };
    if (/Physics/i.test(s.subject)) return { bg: T.goldSoft, fg: T.gold };
    if (/Chem/i.test(s.subject)) return { bg: T.tealSoft, fg: T.teal };
    return { bg: T.goldSoft, fg: T.gold };
  };

  return (
    <div>
      <SectionHeader eyebrow="Academic scheduling" title="Lecturer Time Table"
        action={<div style={{ display: "flex", gap: 8 }}>
          <Btn icon="fas fa-print" onClick={() => showToast("Timetable sent to printer")}>Print</Btn>
          <Btn variant="primary" icon="fas fa-plus" onClick={() => showToast("Opens period assignment editor")}>Assign period</Btn>
        </div>} />

      <div style={{ ...grid3, marginBottom: 18 }}>
        <StatCard label="Periods / week" value={lecturer.periods} sub={lecturer.load + " load"} accent={T.gold} icon="fas fa-calendar-week" />
        <StatCard label="Classes handled" value={lecturer.classes} accent={T.teal} icon="fas fa-users" />
        <StatCard label="Filled slots" value={`${filled}/${totalSlots}`} sub={`${totalSlots - filled} free`} accent={T.info} icon="fas fa-th" />
        <StatCard label="Subjects" value={lecturer.subjects} accent={T.coral} icon="fas fa-book" />
      </div>

      <PillTabs tabs={[["timetable", "Weekly time table"], ["workload", "Workload summary"]]} active={tab} onChange={setTab} />

      {tab === "timetable" && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.slate }}>Lecturer</span>
            <Select value={lecturerId} onChange={(e) => setLecturerId(e.target.value)} style={{ width: "auto", minWidth: 240 }}>
              {lecturers.map((l) => <option key={l.id} value={l.id}>{l.name} — {l.dept}</option>)}
            </Select>
          </div>
          <Card style={{ padding: 18, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 6, fontSize: 12, minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: T.slate }}>Day</th>
                  {TT_PERIODS.map((p) => (
                    <th key={p.id} style={{ padding: "8px 6px", textAlign: "center", minWidth: 110 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>{p.label}</div>
                      <div style={{ fontSize: 10, color: T.slate, fontWeight: 400 }}>{p.time}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TT_DAYS.map((day) => (
                  <tr key={day}>
                    <td style={{ padding: "8px 10px", fontWeight: 700, color: T.ink, background: T.paper, borderRadius: 8 }}>{day}</td>
                    {TT_PERIODS.map((p, i) => {
                      const cell = (grid[day] || [])[i];
                      const c = tint(cell);
                      return (
                        <td key={p.id} style={{ verticalAlign: "top" }}>
                          <div style={{ borderRadius: 9, padding: "8px 9px", minHeight: 52, background: c.bg, border: `1px solid ${cell ? "transparent" : T.rule}`, borderStyle: cell ? "solid" : "dashed" }}>
                            {cell ? (
                              <>
                                <div style={{ fontSize: 12, fontWeight: 700, color: c.fg }}>{cell.subject}</div>
                                <div style={{ fontSize: 11, color: T.ink, marginTop: 2 }}>{cell.cls}</div>
                                <div style={{ fontSize: 10, color: T.slate }}>Room {cell.room}</div>
                              </>
                            ) : (
                              <div style={{ fontSize: 11, color: T.slate, textAlign: "center", paddingTop: 14 }}>Free</div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {tab === "workload" && (
        <Card style={{ padding: 18 }}>
          <DataTable columns={[
            { key: "name", label: "Lecturer", render: (r) => <span style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={r.name} size={30} />{r.name}</span> },
            { key: "dept", label: "Department" },
            { key: "subjects", label: "Subjects" },
            { key: "classes", label: "Classes" },
            { key: "periods", label: "Periods / week" },
            { key: "load", label: "Load", render: (r) => <StatusBadge status={r.load} /> },
          ]} data={LECTURER_WORKLOAD} searchKeys={["name", "dept", "subjects"]}
            filters={[{ key: "dept", options: [...new Set(LECTURER_WORKLOAD.map((l) => l.dept))] }, { key: "load", options: ["High", "Optimal", "Light"] }]} />
        </Card>
      )}
    </div>
  );
}

/* ─── Shared pill-tab strip used inside sub-modules ──────────────── */
function PillTabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
      {tabs.map(([k, l]) => (
        <button key={k} onClick={() => onChange(k)} style={{ padding: "8px 16px", borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: "pointer", border: `1px solid ${active === k ? T.gold : T.rule}`, background: active === k ? T.goldSoft : T.panel, color: active === k ? T.gold : T.slate }}>{l}</button>
      ))}
    </div>
  );
}

/* ================================================================
   MAIN CONTAINER — sidebar-driven tab routing
================================================================ */
function HRManagementMain(props) {
  const [employees, setEmployees] = useState(EMPLOYEES);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = "success") => setToast({ msg, type });

  const tabParam = props.match && props.match.params && props.match.params.tab;
  const activeTab = HR_TAB_KEYS.includes(tabParam) ? tabParam : "dashboard";
  const navigate = (key) => props.history.push(`/hr-management/${key}`);
  const meta = HR_TABS.find((t) => t.key === activeTab);

  return (
    <div style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)", minHeight: "100vh", padding: "24px 28px" }}>
      <style>{`
        @media (max-width: 991px){ .hr-grid-2-1{ grid-template-columns: 1fr !important; } .hr-span-2{ grid-column: auto !important; } }
      `}</style>
      <Container fluid className="px-0">
        <div style={{ background: T.panel, borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.05)", overflow: "hidden", border: `1px solid ${T.rule}` }}>
          {/* Sub-header showing active feature + horizontal quick-switch tabs */}
          <div style={{ background: "#f8fafc", borderBottom: `1.5px solid ${T.rule}`, padding: "14px 22px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><i className="fas fa-user-tie" /></div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>HR Management</div>
                <div style={{ fontSize: 11.5, color: T.slate }}>{meta ? meta.label : ""}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }}>
              {HR_TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button key={tab.key} onClick={() => navigate(tab.key)} style={{
                    display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 9, fontSize: 13, fontWeight: 600,
                    whiteSpace: "nowrap", cursor: "pointer", border: `1px solid ${isActive ? T.ink : T.rule}`,
                    background: isActive ? T.ink : "#fff", color: isActive ? "#fff" : T.slate,
                  }}>
                    <i className={tab.icon} />{tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ padding: 26 }}>
            {activeTab === "dashboard" && <DashboardPanel navigate={navigate} showToast={showToast} />}
            {activeTab === "employees" && <EmployeesPanel employees={employees} setEmployees={setEmployees} showToast={showToast} />}
            {activeTab === "departments" && <DepartmentsPanel showToast={showToast} />}
            {activeTab === "attendance" && <AttendancePanel employees={employees} showToast={showToast} />}
            {activeTab === "timetable" && <TimeTablePanel showToast={showToast} />}
            {activeTab === "leave" && <LeavePanel showToast={showToast} />}
            {activeTab === "payroll" && <PayrollPanel employees={employees} showToast={showToast} />}
            {activeTab === "performance" && <PerformancePanel showToast={showToast} />}
            {activeTab === "recruitment" && <RecruitmentPanel showToast={showToast} />}
            {activeTab === "documents" && <DocumentsPanel showToast={showToast} />}
            {activeTab === "ess" && <SelfServicePanel employees={employees} showToast={showToast} />}
            {activeTab === "reports" && <ReportsPanel showToast={showToast} />}
            {activeTab === "exit" && <ExitPanel showToast={showToast} />}
          </div>
        </div>
      </Container>
      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  );
}

export default withRouter(HRManagementMain);
